import type { UserAccount } from '@repo/types/account';
import { WeModApiClient } from './api-client';

const apiClient = new WeModApiClient(process.env.NEXT_PUBLIC_WEMOD_API_URL ?? '');

export async function getAccount(token?: string): Promise<UserAccount | undefined> {
    const headers: Record<string, string> = {};

    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    return await apiClient.get<UserAccount>('/v3/account', {
        headers,
        credentials: 'include',
    });
}

export type UserRegisterBody = {
    email: string;
    password: string;
    passwordConfirmation: string;
    turnstileToken: string;
    agreement: boolean;
    signupOs?: 'windows' | 'nonwindows';
};

export type UserRegisterContext = {
    cookieHeader?: string;
    acceptLanguage?: string;
    userAgent?: string;
};

export async function userRegister(body: UserRegisterBody, context: UserRegisterContext = {}): Promise<Response> {
    const baseUrl = process.env.NEXT_PUBLIC_WAND_WEBSITE_URL;

    const headers: Record<string, string> = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    };

    if (context.cookieHeader) headers.Cookie = context.cookieHeader;
    if (context.acceptLanguage) headers['Accept-Language'] = context.acceptLanguage;
    if (context.userAgent) headers['User-Agent'] = context.userAgent;

    return fetch(`${baseUrl}/account/register`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            client_id: 'webview',
            email: body.email,
            password: body.password,
            password_confirmation: body.passwordConfirmation,
            'cf-turnstile-response': body.turnstileToken,
            agreement: body.agreement,
            signup_os: body.signupOs,
        }),
    });
}

export async function userLogin(emailOrUsername: string, password: string, turnstileToken: string): Promise<Response> {
    const baseUrl = process.env.NEXT_PUBLIC_WAND_WEBSITE_URL;

    // Return the full Response (rather than throwing on !ok) so callers can
    // classify error statuses — e.g. 401 vs 429 vs 5xx — instead of collapsing
    // every failure into a single thrown error.
    return fetch(`${baseUrl}/account/token`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
            grant_type: 'app_password',
            client_id: 'webview',
            username: emailOrUsername,
            password,
            turnstile_token: turnstileToken,
        }),
    });
}

export async function refreshToken(): Promise<Response> {
    const response = await fetch(`${process.env.NEXT_PUBLIC_WAND_WEBSITE_URL}/account/token`, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        credentials: 'include',
    });

    if (!response.ok) {
        const message = await response.text();
        throw new Error(`Login failed: ${response.status} ${message}`);
    }

    return response;
}
